/**
 * 참가자 화면.
 *
 * 접속하는 즉시 WS를 열고 영어 자막을 보여 준다. 음성은 기본 꺼짐이고, 아래 도크의 토글을
 * 눌러야 켜진다. 꺼져 있는 동안 서버는 이 연결에 오디오를 보내지 않는다. 모바일은 사용자
 * 제스처 없이 `AudioContext`가 소리를 내지 않으므로 잠금 해제는 토글 탭 안에서 한다.
 *
 * 이 화면은 **마이크를 요구하지 않는다** (`getUserMedia` 호출 없음).
 *
 * - 자막 로그: 번역(영어) 크게, 원문(한국어) 작게 토글, 최근 50건, 증분 append 렌더.
 *   가장 최근 번역 줄이 "지금 문장"(`.now`)이다. 원문은 도착 순서대로 끼워 넣을 뿐
 *   번역과 짝을 맞추지 않는다 — 입력·출력 seq가 서로 달라 정확한 짝을 알 수 없다.
 * - 아래 도크: 음성 · 원문 보기 · 글자 크기(보통/크게/아주 크게, localStorage 기억)
 * - 화면 꺼짐은 Screen Wake Lock으로, 무음 스위치는 음성을 켤 때
 *   `navigator.audioSession.type = 'playback'`(Safari 16.4+) + 무음 WAV 루프 `<audio>` 재생으로
 *   막는다. 두 API가 모두 없거나 재생 문제(drop)가 생겼을 때만 도크 위에 안내 한 줄을 보인다.
 * - 자동 스크롤(사용자가 위로 올리면 일시 정지), 새 자막 하이라이트
 * - 재접속: `ws-client.ts` 백오프 + 50분 순환. 재접속 후 서버가 최근 확정 자막 20건을
 *   다시 보내므로 **seq 기준으로 중복을 거른다**. seq는 세션마다 새로 시작하므로
 *   세션 id가 바뀌면 중복 판정 기록을 비운다.
 */
import '../shared/style.css';
import { WsClient } from '../shared/ws-client';
import { OPUS_FRAME_MS, PING_INTERVAL_MS } from '../shared/protocol';
import type {
  CaptionKind,
  SessionState,
  UnknownMessage,
  ViewerAudioFormat,
  ViewerClientKind,
} from '../shared/protocol';
import { decodeAudioFrame, decodeOpusFrame } from '../shared/pcm';
import { TranslationPlayer } from '../shared/player';

/** 화면에 남겨 두는 자막 줄 수 */
const MAX_LINES = 50;
/** 확정 seq 기억 한도. 중복 판정용이라 화면 줄 수보다 넉넉히 둔다. */
const MAX_SEEN_SEQ = 500;
/** 재생 상태 갱신 주기 */
const STATS_INTERVAL_MS = 500;
/** 새 자막 하이라이트 지속 시간. style.css의 `caption-fresh` 애니메이션과 맞춘다. */
const HIGHLIGHT_MS = 1500;
/** 자동 스크롤을 유지할 바닥 여유(px) */
const BOTTOM_SLACK_PX = 48;
/** 글자 크기 단계. style.css의 `body.viewer[data-text-size]`와 맞춘다. */
const TEXT_SIZES = ['normal', 'large', 'xlarge'] as const;
type TextSize = (typeof TEXT_SIZES)[number];
const TEXT_SIZE_KEY = 'ymc.viewerTextSize';
/** 예외일 때만 보이는 안내 한 줄 */
const SOUND_HINT = 'Sound not playing? Turn off silent mode and keep the screen on';
const TEXT_SIZE_LABEL: Record<TextSize, string> = {
  normal: 'Normal',
  large: 'Large',
  xlarge: 'Extra large',
};

function requireEl<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (el === null) throw new Error(`요소를 찾을 수 없습니다: #${id}`);
  return el as T;
}

const badgeEl = requireEl('state-badge');
const stateTextEl = requireEl('state-text');
const bannerEl = requireEl('error-banner');
const soundEl = requireEl<HTMLButtonElement>('sound');
const soundIconEl = requireEl('sound-icon');
const soundLabelEl = requireEl('sound-label');
const textSizeEl = requireEl<HTMLButtonElement>('text-size');
const originEl = requireEl<HTMLButtonElement>('toggle-origin');
const leaveEl = requireEl<HTMLButtonElement>('leave');
const leaveIconEl = requireEl('leave-icon');
const leaveLabelEl = requireEl('leave-label');
const logEl = requireEl('caption-log');
const jumpEl = requireEl<HTMLButtonElement>('scroll-bottom');
const playInfoEl = requireEl('play-info');
const unmuteAudioEl = requireEl<HTMLAudioElement>('unmute-audio');

const player = new TranslationPlayer();

let client: WsClient | null = null;
let pingTimer: number | null = null;
let statsTimer: number | null = null;
let sessionState: SessionState = 'idle';
/** 마지막으로 본 세션 id. 세션이 끝나도 지우지 않는다 — 다음 세션과 비교해야 한다. */
let lastSessionId: string | null = null;
let wsOpen = false;
let sessionEnded = false;
let autoScroll = true;
/** 음성이 켜져 있는가. 재접속해도 유지되고, 연결이 열릴 때마다 서버에 다시 알린다. */
let soundOn = false;
/** 이 기기에서만 수신을 멈췄는가. 통역 자체를 끄는 것은 운영자만 한다. */
let stoppedHere = false;

// ---------------------------------------------------------------------------
// 상태 배지
// ---------------------------------------------------------------------------

const STATE_LABEL: Record<SessionState, string> = {
  idle: 'Waiting to start',
  connecting: 'Getting ready…',
  live: 'Live',
  reconnecting: 'Reconnecting…',
  stopping: 'Ending…',
};

function setStatus(state: string, text: string): void {
  badgeEl.dataset['state'] = state;
  stateTextEl.textContent = text;
}

function renderBadge(): void {
  if (stoppedHere) {
    setStatus('ended', 'Stopped');
    return;
  }
  if (!wsOpen) {
    setStatus('reconnecting', 'Connecting…');
    return;
  }
  if (sessionEnded) {
    setStatus('ended', 'The session has ended');
    return;
  }
  setStatus(sessionState, STATE_LABEL[sessionState]);
}

function showBanner(message: string): void {
  bannerEl.textContent = message;
  bannerEl.hidden = false;
}

function hideBanner(): void {
  bannerEl.hidden = true;
}

// ---------------------------------------------------------------------------
// 자막 (증분 append 렌더 + seq 기준 중복 제거)
// ---------------------------------------------------------------------------

interface Line {
  el: HTMLElement;
  text: string;
}

/** 화면에 살아 있는 줄 */
const lines = new Map<number, Line>();
/** 이미 확정으로 받은 seq. 재접속 재전송을 여기서 거른다. */
const finalizedSeq = new Set<number>();
const finalizedOrder: number[] = [];
/** 지금 문장 — 가장 최근에 시작된 번역 줄 */
let nowEl: HTMLElement | null = null;

function markFinalized(seq: number): void {
  if (finalizedSeq.has(seq)) return;
  finalizedSeq.add(seq);
  finalizedOrder.push(seq);
  while (finalizedOrder.length > MAX_SEEN_SEQ) {
    const oldest = finalizedOrder.shift();
    if (oldest !== undefined) finalizedSeq.delete(oldest);
  }
}

/** seq는 세션마다 새로 시작한다. 새 세션의 자막이 옛 세션 seq와 겹쳐 버려지지 않게 비운다. */
function resetCaptionTracking(): void {
  lines.clear();
  finalizedSeq.clear();
  finalizedOrder.length = 0;
}

function renderCaption(seq: number, kind: CaptionKind, text: string, final: boolean, append: boolean): void {
  // 이미 확정으로 받은 자막이 다시 왔다 → 재접속 재전송. 화면에는 한 번만 남긴다.
  if (finalizedSeq.has(seq)) return;

  let line = lines.get(seq);
  if (line === undefined) {
    const el = document.createElement('p');
    el.className = 'caption';
    el.dataset['kind'] = kind;
    line = { el, text: '' };
    lines.set(seq, line);
    logEl.append(el);
    if (kind === 'output') {
      nowEl?.classList.remove('now');
      el.classList.add('now');
      nowEl = el;
    }
    // 최근 50건만 남긴다.
    while (logEl.childElementCount > MAX_LINES) {
      const first = logEl.firstElementChild;
      if (first === null) break;
      for (const [key, value] of lines) {
        if (value.el === first) {
          lines.delete(key);
          break;
        }
      }
      first.remove();
    }
  }

  line.text = append ? line.text + text : text;
  line.el.textContent = line.text;

  if (final) {
    markFinalized(seq);
    lines.delete(seq);
    line.el.dataset['final'] = 'true';
    // 새 자막 하이라이트. 끝나면 클래스를 뗀다 — 남겨 두면 원문 토글로 다시 표시될 때
    // (display:none → block) 옛 줄까지 한꺼번에 다시 번쩍인다.
    const el = line.el;
    el.classList.remove('fresh');
    void el.offsetWidth;
    el.classList.add('fresh');
    window.setTimeout(() => el.classList.remove('fresh'), HIGHLIGHT_MS);
  }

  if (line.text.trim() === '') line.el.hidden = true;
  else line.el.hidden = false;

  if (autoScroll) scrollToBottom();
}

function scrollToBottom(): void {
  logEl.scrollTop = logEl.scrollHeight;
  autoScroll = true;
  jumpEl.hidden = true;
}

logEl.addEventListener('scroll', () => {
  const atBottom = logEl.scrollHeight - logEl.scrollTop - logEl.clientHeight < BOTTOM_SLACK_PX;
  autoScroll = atBottom;
  jumpEl.hidden = atBottom;
});

jumpEl.addEventListener('click', () => scrollToBottom());

// ---------------------------------------------------------------------------
// 토글
// ---------------------------------------------------------------------------

function applyOriginLabel(on: boolean): void {
  const label = on ? 'Hide Korean' : 'Show Korean';
  originEl.setAttribute('aria-label', label);
  originEl.title = label;
}

originEl.addEventListener('click', () => {
  const on = originEl.dataset['on'] !== 'true';
  originEl.dataset['on'] = String(on);
  originEl.setAttribute('aria-pressed', String(on));
  applyOriginLabel(on);
  logEl.dataset['origin'] = on ? 'on' : 'off';
  if (autoScroll) scrollToBottom();
});

// ---------------------------------------------------------------------------
// 음성 토글
// ---------------------------------------------------------------------------

function renderSound(): void {
  soundEl.dataset['on'] = String(soundOn);
  soundEl.setAttribute('aria-pressed', String(soundOn));
  soundIconEl.textContent = soundOn ? '🔊' : '🔇';
  soundLabelEl.textContent = soundOn ? 'Sound on' : 'Sound off';
  const label = soundOn ? 'Turn sound off' : 'Turn sound on';
  soundEl.setAttribute('aria-label', label);
  soundEl.title = label;
  if ('mediaSession' in navigator) navigator.mediaSession.playbackState = soundOn ? 'playing' : 'none';
}

/**
 * 음성을 켠다. **토글 탭 안에서 불려야 한다** — 아래 세 줄이 await 없이 제스처 안에서
 * 순서대로 돌아야 iOS가 오디오를 풀어 준다.
 */
function turnSoundOn(): void {
  if (soundOn) return;
  soundEl.disabled = true;
  claimPlaybackSession();
  playSilentLoop();
  setupMediaSession();
  player
    .ensureContext()
    .then(() => {
      soundOn = true;
      hideBanner();
      renderSound();
      client?.send({ type: 'listen', on: true });
      if (statsTimer !== null) window.clearInterval(statsTimer);
      statsTimer = window.setInterval(renderPlayStats, STATS_INTERVAL_MS);
    })
    .catch((err: unknown) => {
      stopSilentLoop();
      clearMediaSession();
      showBanner(`Could not turn on sound: ${String(err)}`);
    })
    .finally(() => {
      soundEl.disabled = false;
    });
}

function turnSoundOff(): void {
  if (!soundOn) return;
  soundOn = false;
  client?.send({ type: 'listen', on: false });
  player.reset();
  closeOpusDecoder();
  stopSilentLoop();
  clearMediaSession();
  if (statsTimer !== null) window.clearInterval(statsTimer);
  statsTimer = null;
  playInfoEl.hidden = true;
  renderSound();
}

soundEl.addEventListener('click', () => {
  void requestWakeLock();
  if (soundOn) turnSoundOff();
  else turnSoundOn();
});

// ---------------------------------------------------------------------------
// 무음 스위치 우회 · 화면 꺼짐 방지 (안내 대신 코드로)
// ---------------------------------------------------------------------------

interface AudioSessionLike {
  type: string;
}
/** Safari 16.4+의 Audio Session API. 없으면 무음 루프만으로 버틴다. */
const audioSession = (navigator as Navigator & { audioSession?: AudioSessionLike }).audioSession;
const hasWakeLock = 'wakeLock' in navigator && navigator.wakeLock !== undefined;
/** 두 API가 모두 없으면(구형 iOS 등) 무음 해제·화면 켜 두기 안내를 계속 보인다. */
const legacyAudio = audioSession === undefined && !hasWakeLock;

/** 0.25초짜리 8kHz 8비트 무음 WAV (data URI, 약 3KB). */
function silentWavDataUri(): string {
  const rate = 8000;
  const samples = rate / 4;
  const bytes = new Uint8Array(44 + samples);
  const view = new DataView(bytes.buffer);
  const ascii = (at: number, text: string): void => {
    for (let i = 0; i < text.length; i += 1) bytes[at + i] = text.charCodeAt(i);
  };
  ascii(0, 'RIFF');
  view.setUint32(4, 36 + samples, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true); // fmt 청크 크기
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // 모노
  view.setUint32(24, rate, true);
  view.setUint32(28, rate, true); // 바이트/초
  view.setUint16(32, 1, true); // 블록 정렬
  view.setUint16(34, 8, true); // 8비트
  ascii(36, 'data');
  view.setUint32(40, samples, true);
  bytes.fill(128, 44); // 8비트 PCM의 무음은 128
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return `data:audio/wav;base64,${btoa(bin)}`;
}
unmuteAudioEl.src = silentWavDataUri();

/** 오디오 세션을 미디어 재생으로 옮긴다 — 무음 스위치를 무시하게 된다. */
function claimPlaybackSession(): void {
  if (audioSession === undefined) return;
  try {
    audioSession.type = 'playback';
  } catch (err) {
    console.info(`[ymc] audioSession 설정 실패(무시): ${String(err)}`);
  }
}

function playSilentLoop(): void {
  unmuteAudioEl.play().catch((err: unknown) => {
    console.info(`[ymc] 무음 루프 재생 실패(무시): ${String(err)}`);
  });
}

function stopSilentLoop(): void {
  unmuteAudioEl.pause();
}

let wakeLock: WakeLockSentinel | null = null;
let wakeLockPending = false;

async function requestWakeLock(): Promise<void> {
  if (!hasWakeLock || wakeLock !== null || wakeLockPending || document.visibilityState !== 'visible') return;
  wakeLockPending = true;
  try {
    const sentinel = await navigator.wakeLock.request('screen');
    wakeLock = sentinel;
    // 화면이 가려지면 브라우저가 스스로 푼다. 다시 보이면 visibilitychange에서 다시 잡는다.
    sentinel.addEventListener('release', () => {
      if (wakeLock === sentinel) wakeLock = null;
      console.info('[ymc] 화면 켜 두기(wake lock) 해제');
    });
    console.info('[ymc] 화면 켜 두기(wake lock) 켜짐');
  } catch (err) {
    console.info(`[ymc] 화면 켜 두기 요청 실패(무시): ${String(err)}`);
  } finally {
    wakeLockPending = false;
  }
}

function releaseWakeLock(): void {
  const sentinel = wakeLock;
  wakeLock = null;
  if (sentinel !== null) sentinel.release().catch(() => undefined);
}

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && !sessionEnded && !stoppedHere) void requestWakeLock();
});
// 일부 브라우저는 사용자 동작 없이는 화면 켜 두기를 거절한다. 첫 터치에서 다시 청한다.
document.addEventListener('pointerdown', () => void requestWakeLock(), { once: true });

/** 저장소가 막힌 브라우저(사생활 보호 모드 등)에서도 화면은 그대로 동작해야 한다. */
function readTextSize(): TextSize {
  try {
    const saved = window.localStorage.getItem(TEXT_SIZE_KEY);
    return (TEXT_SIZES as readonly string[]).includes(saved ?? '') ? (saved as TextSize) : 'normal';
  } catch {
    return 'normal';
  }
}

function applyTextSize(size: TextSize): void {
  document.body.dataset['textSize'] = size;
  const label = `Text size: ${TEXT_SIZE_LABEL[size]}`;
  textSizeEl.dataset['level'] = size;
  textSizeEl.setAttribute('aria-label', label);
  textSizeEl.title = label;
}

textSizeEl.addEventListener('click', () => {
  const current = TEXT_SIZES.indexOf(readTextSizeFromBody());
  const next = TEXT_SIZES[(current + 1) % TEXT_SIZES.length] ?? 'normal';
  applyTextSize(next);
  try {
    window.localStorage.setItem(TEXT_SIZE_KEY, next);
  } catch {
    // 기억하지 못해도 이번 화면에서는 바뀐 크기로 보인다.
  }
  if (autoScroll) scrollToBottom();
});

function readTextSizeFromBody(): TextSize {
  const v = document.body.dataset['textSize'];
  return (TEXT_SIZES as readonly string[]).includes(v ?? '') ? (v as TextSize) : 'normal';
}

applyTextSize(readTextSize());

// ---------------------------------------------------------------------------
// Opus 해제 능력 탐지
// ---------------------------------------------------------------------------

/**
 * 능력 탐지 제한 시간. `isConfigSupported()`가 끝내 답하지 않는 기기를 대비한 안전망이다.
 * 시간을 넘기면 능력 없음(pcm)으로 보고한다 — 모르면 안 되는 쪽이 아니라 안전한 쪽으로 간다.
 */
const OPUS_PROBE_TIMEOUT_MS = 1000;

/** 이 파일이 쓰는 만큼만 좁힌 WebCodecs 선언. lib.dom 유무에 기대지 않는다. */
interface AudioDecoderSupport {
  supported?: boolean;
}
interface AudioDecoderLike {
  isConfigSupported?: (config: {
    codec: string;
    sampleRate: number;
    numberOfChannels: number;
  }) => Promise<AudioDecoderSupport>;
}

/**
 * `AudioDecoder`가 있다는 것만으로는 부족하다 — 실제로 우리가 쓸 설정
 * `{codec:'opus', sampleRate:48000, numberOfChannels:1}` 이 허용될 때만 true다.
 */
async function detectOpusSupport(): Promise<boolean> {
  const decoder = (globalThis as { AudioDecoder?: AudioDecoderLike }).AudioDecoder;
  if (decoder === undefined || typeof decoder.isConfigSupported !== 'function') return false;
  try {
    const probe = decoder.isConfigSupported({
      codec: 'opus',
      sampleRate: 48000,
      numberOfChannels: 1,
    });
    const timeout = new Promise<null>((resolve) => {
      window.setTimeout(() => resolve(null), OPUS_PROBE_TIMEOUT_MS);
    });
    const result = await Promise.race([probe, timeout]);
    return result !== null && result.supported === true;
  } catch {
    // 설정을 거절하는 기기는 예외를 던지기도 한다. 어느 쪽이든 능력 없음이다.
    return false;
  }
}

/**
 * 탐지는 화면이 뜰 때 한 번만 시작하고 결과를 재접속마다 재사용한다.
 * 탐지가 끝나기 전 연결이 열리면 `hello`가 그만큼 늦는데, 서버는 hello 전을
 * 무조건 pcm으로 두므로 그동안에도 안전하다.
 */
const opusSupport: Promise<boolean> = detectOpusSupport();

// ---------------------------------------------------------------------------
// Opus 디코드
// ---------------------------------------------------------------------------

/** 한 패킷이 담는 오디오 길이(마이크로초). EncodedAudioChunk의 timestamp 단위다. */
const OPUS_FRAME_US = OPUS_FRAME_MS * 1000;

/** 디코더 출력 샘플레이트. Opus는 규격상 내부 표현이 48kHz다 */
const OPUS_DECODE_SAMPLE_RATE = 48000;

/** 쓰는 만큼만 좁힌 WebCodecs 선언. 능력 탐지와 마찬가지로 lib.dom 유무에 기대지 않는다. */
interface AudioDataLike {
  readonly numberOfFrames: number;
  readonly sampleRate: number;
  copyTo(dest: Float32Array, options: { planeIndex: number; format: string }): void;
  close(): void;
}
interface AudioDecoderInstance {
  decode(chunk: object): void;
  close(): void;
  configure(config: { codec: string; sampleRate: number; numberOfChannels: number }): void;
}
interface AudioDecoderCtor {
  new (init: { output: (data: AudioDataLike) => void; error: (err: unknown) => void }): AudioDecoderInstance;
}
interface EncodedAudioChunkCtor {
  new (init: { type: 'key'; timestamp: number; duration: number; data: BufferSource }): object;
}

/** 서버가 마지막으로 알린 이 연결의 바이너리 형식. hello 전·재접속 직후는 pcm. */
let audioFormat: ViewerAudioFormat = 'pcm';

/** 세션당 1개로 유지하는 디코더. 청크마다 다시 만들면 6.5ms씩 사라진다. */
let opusDecoder: AudioDecoderInstance | null = null;

/** 디코더에 넣은 누적 시각(µs). 디코더를 새로 만들 때만 0으로 돌아간다. */
let opusTimestampUs = 0;

/**
 * 이 기기에서 Opus를 포기했는가. 한 번 실패한 기기는 재접속해도 다시 시도하지
 * 않는다 — 형식이 오가는 것이 처음부터 PCM인 것보다 나쁘다.
 */
let opusGivenUp = false;

function closeOpusDecoder(): void {
  const decoder = opusDecoder;
  opusDecoder = null;
  opusTimestampUs = 0;
  if (decoder === null) return;
  try {
    decoder.close();
  } catch {
    // 이미 닫혔다.
  }
}

/**
 * 디코드가 실패했다. 스스로 pcm으로 내려가고 서버에도 능력 없음을 다시 알린다.
 * 사용자에게는 알리지 않는다. 소리가 계속 들리는 것이 전부다.
 */
function giveUpOpus(reason: string): void {
  if (opusGivenUp && audioFormat === 'pcm') return;
  opusGivenUp = true;
  audioFormat = 'pcm';
  closeOpusDecoder();
  console.warn(`[viewer] Opus 디코드를 포기하고 PCM으로 내려갑니다: ${reason}`);
  client?.send({ type: 'hello', client: detectClient(), audio: { opus: false } });
}

function ensureOpusDecoder(): AudioDecoderInstance | null {
  if (opusDecoder !== null) return opusDecoder;
  const Ctor = (globalThis as { AudioDecoder?: AudioDecoderCtor }).AudioDecoder;
  if (Ctor === undefined) {
    giveUpOpus('AudioDecoder가 없습니다');
    return null;
  }
  try {
    const decoder = new Ctor({
      output: (data) => {
        try {
          // AudioData는 f32-planar를 준다. Float32 그대로 넘겨 왕복 손실을 없앤다.
          const samples = new Float32Array(data.numberOfFrames);
          data.copyTo(samples, { planeIndex: 0, format: 'f32-planar' });
          player.enqueueFloat32(samples, data.sampleRate);
        } catch (err) {
          giveUpOpus(`출력 복사 실패: ${String(err)}`);
        } finally {
          data.close();
        }
      },
      error: (err) => giveUpOpus(`디코더 오류: ${String(err)}`),
    });
    decoder.configure({
      codec: 'opus',
      sampleRate: OPUS_DECODE_SAMPLE_RATE,
      numberOfChannels: 1,
    });
    opusDecoder = decoder;
    opusTimestampUs = 0;
    return decoder;
  } catch (err) {
    giveUpOpus(`디코더를 만들지 못했습니다: ${String(err)}`);
    return null;
  }
}

/** Opus 바이너리 프레임을 뜯어 패킷 단위로 디코더에 넣는다. */
function handleOpusBinary(data: ArrayBuffer): void {
  const frame = decodeOpusFrame(data);
  // 규약에 맞지 않는 프레임은 조용히 버린다. 형식 경계를 잘못 읽었을 때 잡음을 내지 않는다.
  if (frame === null) return;

  const Chunk = (globalThis as { EncodedAudioChunk?: EncodedAudioChunkCtor }).EncodedAudioChunk;
  if (Chunk === undefined) {
    giveUpOpus('EncodedAudioChunk가 없습니다');
    return;
  }
  const decoder = ensureOpusDecoder();
  if (decoder === null) return;

  try {
    for (const packet of frame.packets) {
      decoder.decode(
        new Chunk({ type: 'key', timestamp: opusTimestampUs, duration: OPUS_FRAME_US, data: packet }),
      );
      opusTimestampUs += OPUS_FRAME_US;
    }
  } catch (err) {
    giveUpOpus(`decode() 예외: ${String(err)}`);
  }
}

/** hello.client는 서버 로그와 operator의 플랫폼별 집계에만 쓴다. */
function detectClient(): ViewerClientKind {
  const ua = navigator.userAgent;
  // iPadOS 13+ Safari는 UA에 iPad 대신 Macintosh를 쓰고 터치를 지원한다.
  const iPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/.test(ua) || iPadOS) return 'ios-safari';
  if (/Android/.test(ua)) return 'android-chrome';
  return 'other';
}

function connect(): void {
  const ws = new WsClient('/ws/viewer', {
    onStatus: (status) => {
      wsOpen = status === 'open';
      renderBadge();
    },
    onOpen: () => {
      wsOpen = true;
      sessionEnded = false;
      hideBanner();
      renderBadge();
      void requestWakeLock();
      // 새 연결은 hello 전까지 무조건 pcm이다. 디코더도 여기서 버린다 — 연결이
      // 바뀌면 패킷 흐름이 끊기므로 상태를 이어 갈 이유가 없다(손실은 preSkip 6.5ms 1회).
      audioFormat = 'pcm';
      closeOpusDecoder();
      // 능력 탐지가 비동기라 hello가 한 틱 늦는다. 서버는 그동안 pcm으로 둔다.
      void opusSupport.then((opus) => {
        // 늦게 도착한 결과가 이미 닫힌 소켓으로 나가지 않게 한다. send()도 readyState를 본다.
        if (client === ws) {
          ws.send({ type: 'hello', client: detectClient(), audio: { opus: opus && !opusGivenUp } });
          // 서버는 연결마다 음성 꺼짐으로 시작한다. 켜 둔 상태를 다시 알린다.
          if (soundOn) ws.send({ type: 'listen', on: true });
        }
      });
      if (pingTimer !== null) window.clearInterval(pingTimer);
      pingTimer = window.setInterval(() => ws.send({ type: 'ping' }), PING_INTERVAL_MS);
    },
    onMessage: (msg: UnknownMessage) => {
      switch (msg.type) {
        case 'state': {
          const state = msg['state'];
          sessionState = typeof state === 'string' ? (state as SessionState) : 'idle';
          const id = msg['sessionId'];
          if (typeof id === 'string') {
            if (id !== lastSessionId) resetCaptionTracking();
            lastSessionId = id;
          }
          if (sessionState !== 'idle') {
            sessionEnded = false;
            void requestWakeLock();
          }
          renderBadge();
          return;
        }
        case 'caption': {
          const seq = msg['seq'];
          const kind = msg['kind'];
          const text = msg['text'];
          if (typeof seq !== 'number' || typeof text !== 'string') return;
          renderCaption(
            seq,
            kind === 'input' ? 'input' : 'output',
            text,
            msg['final'] === true,
            msg['append'] === true,
          );
          return;
        }
        case 'audioFormat': {
          // 이 메시지 **다음에 오는** 바이너리부터 이 형식이다.
          const next: ViewerAudioFormat = msg['format'] === 'opus' ? 'opus' : 'pcm';
          if (next === audioFormat) return;
          audioFormat = next;
          // pcm으로 내려가면 디코더를 버린다. opus로 올라갈 때는 첫 프레임에서 만든다.
          if (next === 'pcm') closeOpusDecoder();
          return;
        }
        case 'error':
          // 서버가 viewer에게 보내는 오류는 세션 종료뿐이다. 다음 세션을 위해 음성 토글은 그대로 둔다.
          if (msg['code'] === 'session_ended') {
            sessionEnded = true;
            player.reset();
            releaseWakeLock();
            renderBadge();
          }
          return;
        case 'pong':
          return;
        default:
          return;
      }
    },
    onBinary: (data) => {
      // 토글을 끈 뒤 소켓에 남아 있던 프레임은 버린다.
      if (!soundOn) return;
      if (audioFormat === 'opus') {
        handleOpusBinary(data);
        return;
      }
      const frame = decodeAudioFrame(data);
      if (frame === null) return;
      player.enqueue(frame.pcm);
    },
    onCycleDue: () => cycle(),
  });
  client = ws;
  ws.connect();
}

/** 50분 선제 순환. viewer는 보낼 상태가 없으므로 바로 다시 붙는다. */
function cycle(): void {
  const ws = client;
  if (ws === null) return;
  ws.close(1000, 'cycle');
  window.setTimeout(() => ws.connect(), 200);
}

function disconnect(): void {
  turnSoundOff();
  releaseWakeLock();
  if (pingTimer !== null) window.clearInterval(pingTimer);
  pingTimer = null;
  const ws = client;
  client = null;
  wsOpen = false;
  ws?.close(1000, 'viewer left'); // stopped=true → 자동 재접속하지 않는다
}

// 이 기기만 멈춘다. 연결을 끊을 뿐이라 통역과 다른 참가자에게는 영향이 없다.
// 이미 받은 자막은 화면에 남고, 다시 연결하면 서버가 최근 자막을 재전송한다.
leaveEl.addEventListener('click', () => {
  stoppedHere = !stoppedHere;
  leaveIconEl.textContent = stoppedHere ? '▶️' : '⏹';
  leaveLabelEl.textContent = stoppedHere ? 'Resume' : 'Stop';
  const label = stoppedHere ? 'Resume captions on this device' : 'Stop captions on this device';
  leaveEl.setAttribute('aria-label', label);
  leaveEl.title = label;
  soundEl.disabled = stoppedHere;
  if (stoppedHere) disconnect();
  else connect();
  renderBadge();
});

// 뒤로 가기·다른 페이지로 떠나면 깨끗이 정리하고, 뒤로 가기 캐시에서 되살아나면 다시 연결한다.
window.addEventListener('pagehide', disconnect);
window.addEventListener('pageshow', (ev) => {
  if (ev.persisted && !stoppedHere) connect();
});

// ---------------------------------------------------------------------------
// 잠금 화면 재생 컨트롤 (Media Session)
// ---------------------------------------------------------------------------
// 잠금 화면 컨트롤은 기본적으로 무음 루프 <audio>에 붙는다. 번역 음성은 Web Audio라서
// 핸들러를 직접 달지 않으면 일시정지를 눌러도 무음 루프만 멈추고 음성은 계속 나온다.

function setupMediaSession(): void {
  if (!('mediaSession' in navigator)) return;
  const ms = navigator.mediaSession;
  try {
    ms.metadata = new MediaMetadata({ title: 'YMC & Onyu Small Group Live Translation' });
  } catch {
    // 구형 브라우저 — 제목 없이 진행
  }
  const set = (action: MediaSessionAction, handler: MediaSessionActionHandler | null): void => {
    try {
      ms.setActionHandler(action, handler);
    } catch {
      // 지원하지 않는 동작
    }
  };
  set('play', () => turnSoundOn());
  set('pause', () => turnSoundOff());
  set('stop', () => turnSoundOff());
  // 생방송이라 앞뒤로 건너뛰기·탐색은 없다.
  set('seekbackward', null);
  set('seekforward', null);
  set('seekto', null);
  set('previoustrack', null);
  set('nexttrack', null);
}

function clearMediaSession(): void {
  if (!('mediaSession' in navigator)) return;
  navigator.mediaSession.metadata = null;
  navigator.mediaSession.playbackState = 'none';
}

/** 재생 문제(지연 누적으로 소리를 건너뜀)가 보인 뒤 안내를 남겨 두는 시간 */
const PROBLEM_SHOW_MS = 10_000;
let lastDrops = 0;
let lastProblemAt = 0;

/**
 * 안내 한 줄은 평소에는 숨기고, 예외일 때만 작게 보인다:
 * 무음 스위치·화면 꺼짐을 코드로 막을 수 없는 브라우저(legacyAudio)이거나,
 * 방금 재생 문제(지연 누적으로 소리를 건너뜀)가 있었을 때.
 * 재동기(언더런)는 말 사이 쉼마다 생기는 정상 동작이라 문제로 치지 않는다.
 */
function renderPlayStats(): void {
  const s = player.stats();
  const now = Date.now();
  if (s.drops > lastDrops) lastProblemAt = now;
  lastDrops = s.drops;
  const problem = now - lastProblemAt < PROBLEM_SHOW_MS;
  const show = soundOn && (legacyAudio || problem);
  playInfoEl.hidden = !show;
  if (show) playInfoEl.textContent = problem ? `${SOUND_HINT} (drop ${s.drops})` : SOUND_HINT;
}

renderSound();
renderBadge();
connect();
void requestWakeLock();
